// DocumentAccessModal.js - Expert-level Document Access Control UI
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  X, 
  Check, 
  ChevronDown, 
  ChevronUp,
  ChevronRight,
  Search,
  FileText,
  Loader2,
  Filter,
  CheckCircle2,
  AlertTriangle,
  Keyboard,
  RotateCcw,
  Menu,
  Building2,
  SlidersHorizontal
} from 'lucide-react';

function DocumentAccessModal({ 
  user, 
  documents, 
  onClose, 
  onSave, 
  isLoading, 
  isLoadingDocuments 
}) {
  const [selectedDocuments, setSelectedDocuments] = useState(
    user.allowed_documents || []
  );
  const [organization, setOrganization] = useState(user.organization || '');
  const [promptConfig, setPromptConfig] = useState(user.prompt_config || 'default');
  const [canSelectPrompt, setCanSelectPrompt] = useState(user.can_select_prompt || false);
  const [canCreatePrompts, setCanCreatePrompts] = useState(user.can_create_prompts || false);
  const [showPdf, setShowPdf] = useState(user.show_pdf !== false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSelectionSummary, setShowSelectionSummary] = useState(false);
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  
  // Sort state
  const [sortField, setSortField] = useState('publisher');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Mobile UI state
  const [showMobileControls, setShowMobileControls] = useState(false);

  // Column filters
  const [filters, setFilters] = useState({
    publisher: '',
    title: '',
    reference: '',
    authors: '',
    year: '',
    country: '',
    tag: ''
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Extract unique values for filter dropdowns
  const uniqueValues = useMemo(() => {
    const values = {
      publishers: new Set(),
      authors: new Set(),
      years: new Set(),
      countries: new Set(),
      tags: new Set()
    };

    (documents || []).forEach(doc => {
      if (doc.publishing_body) values.publishers.add(doc.publishing_body);
      if (doc.authors) values.authors.add(doc.authors);
      if (doc.publication_date) {
        const year = doc.publication_date.substring(0, 4);
        if (year && /^\d{4}$/.test(year)) values.years.add(year);
      }
      if (doc.country_of_origin) values.countries.add(doc.country_of_origin);
      if (doc.tags) {
        doc.tags.forEach(tag => values.tags.add(tag));
      }
    });

    return {
      publishers: Array.from(values.publishers).sort(),
      authors: Array.from(values.authors).sort(),
      years: Array.from(values.years).sort().reverse(),
      countries: Array.from(values.countries).sort(),
      tags: Array.from(values.tags).sort()
    };
  }, [documents]);

  // Filter and sort documents
  const filteredAndSortedDocuments = useMemo(() => {
    let result = [...(documents || [])];

    // Apply global search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc => 
        (doc.document_title || '').toLowerCase().includes(query) ||
        (doc.document_reference || '').toLowerCase().includes(query) ||
        (doc.authors || '').toLowerCase().includes(query) ||
        (doc.description || '').toLowerCase().includes(query) ||
        (doc.publishing_body || '').toLowerCase().includes(query) ||
        (doc.canonical_slug || '').toLowerCase().includes(query) ||
        (doc.tags || []).some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Apply column filters
    if (filters.publisher) {
      result = result.filter(doc => doc.publishing_body === filters.publisher);
    }
    if (filters.title) {
      result = result.filter(doc => 
        (doc.document_title || '').toLowerCase().includes(filters.title.toLowerCase())
      );
    }
    if (filters.reference) {
      result = result.filter(doc => 
        (doc.document_reference || '').toLowerCase().includes(filters.reference.toLowerCase())
      );
    }
    if (filters.authors) {
      result = result.filter(doc => doc.authors === filters.authors);
    }
    if (filters.year) {
      result = result.filter(doc => 
        doc.publication_date && doc.publication_date.startsWith(filters.year)
      );
    }
    if (filters.country) {
      result = result.filter(doc => doc.country_of_origin === filters.country);
    }
    if (filters.tag) {
      result = result.filter(doc => (doc.tags || []).includes(filters.tag));
    }

    // Sort
    result.sort((a, b) => {
      let aVal = '', bVal = '';
      switch (sortField) {
        case 'publisher':
          aVal = a.publishing_body || '';
          bVal = b.publishing_body || '';
          break;
        case 'title':
          aVal = a.document_title || a.source_filename || '';
          bVal = b.document_title || b.source_filename || '';
          break;
        case 'reference':
          aVal = a.document_reference || '';
          bVal = b.document_reference || '';
          break;
        case 'authors':
          aVal = a.authors || '';
          bVal = b.authors || '';
          break;
        case 'date':
          aVal = a.publication_date || '';
          bVal = b.publication_date || '';
          break;
        case 'country':
          aVal = a.country_of_origin || '';
          bVal = b.country_of_origin || '';
          break;
        default:
          aVal = a.document_title || '';
          bVal = b.document_title || '';
      }
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [documents, searchQuery, filters, sortField, sortDirection]);

  // Selection summary by publisher
  const selectionSummary = useMemo(() => {
    const summary = {};
    (documents || []).forEach(doc => {
      const publisher = doc.publishing_body || 'Unknown';
      if (!summary[publisher]) {
        summary[publisher] = { total: 0, selected: 0 };
      }
      summary[publisher].total++;
      if (selectedDocuments.includes(doc.canonical_slug)) {
        summary[publisher].selected++;
      }
    });
    return Object.entries(summary)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([publisher, counts]) => ({ publisher, ...counts }));
  }, [documents, selectedDocuments]);

  // Handlers
  const handleToggleDocument = useCallback((slug, index, event) => {
    // Shift+Click for range selection
    if (event?.shiftKey && lastClickedIndex !== null && index !== lastClickedIndex) {
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      const rangeDocuments = filteredAndSortedDocuments.slice(start, end + 1);
      const rangeSlugs = rangeDocuments.map(d => d.canonical_slug);
      
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        rangeSlugs.forEach(s => newSet.add(s));
        return Array.from(newSet);
      });
    } else {
      setSelectedDocuments(prev => 
        prev.includes(slug)
          ? prev.filter(s => s !== slug)
          : [...prev, slug]
      );
    }
    setLastClickedIndex(index);
  }, [filteredAndSortedDocuments, lastClickedIndex]);

  const handleSelectAll = () => {
    const allSlugs = filteredAndSortedDocuments.map(d => d.canonical_slug);
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      allSlugs.forEach(s => newSet.add(s));
      return Array.from(newSet);
    });
  };

  const handleSelectNone = () => {
    const filteredSlugs = new Set(filteredAndSortedDocuments.map(d => d.canonical_slug));
    setSelectedDocuments(prev => prev.filter(s => !filteredSlugs.has(s)));
  };

  const handleSelectAllGlobal = () => {
    setSelectedDocuments((documents || []).map(d => d.canonical_slug));
  };

  const handleClearAllGlobal = () => {
    setSelectedDocuments([]);
  };

  const handleSelectByPublisher = (publisher) => {
    const publisherDocs = (documents || []).filter(d => d.publishing_body === publisher);
    const slugs = publisherDocs.map(d => d.canonical_slug);
    const allSelected = slugs.every(s => selectedDocuments.includes(s));
    
    if (allSelected) {
      setSelectedDocuments(prev => prev.filter(s => !slugs.includes(s)));
    } else {
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        slugs.forEach(s => newSet.add(s));
        return Array.from(newSet);
      });
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const removeFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  const clearAllFilters = () => {
    setFilters({
      publisher: '',
      title: '',
      reference: '',
      authors: '',
      year: '',
      country: '',
      tag: ''
    });
    setSearchQuery('');
  };

  const activeFilters = Object.entries(filters).filter(([_, v]) => v);
  const hasActiveFilters = searchQuery || activeFilters.length > 0;

  const handleSave = () => {
    const documentsToSave = selectedDocuments.length > 0 ? selectedDocuments : null;
    onSave(user.id, documentsToSave, organization || null, promptConfig, canSelectPrompt, canCreatePrompts, showPdf);
  };

  // Stats
  const totalDocuments = documents?.length || 0;
  const filteredCount = filteredAndSortedDocuments.length;
  const selectedCount = selectedDocuments.length;
  const selectedInFiltered = filteredAndSortedDocuments.filter(d => 
    selectedDocuments.includes(d.canonical_slug)
  ).length;
  const allFilteredSelected = filteredCount > 0 && selectedInFiltered === filteredCount;

  // Column Header Component
  const ColumnHeader = ({ field, label, width, filterType, filterOptions }) => {
    const isActive = sortField === field;
    const hasFilter = filters[field];
    
    return (
      <th className={`${width} px-3 py-3 text-left bg-kaleido-gray-50`}>
        <div className="space-y-2">
          <button 
            onClick={() => handleSort(field)}
            className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors ${
              isActive ? 'text-kaleido-purple' : 'text-kaleido-gray-500 hover:text-kaleido-gray-700'
            }`}
          >
            {label}
            {isActive && (sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
          </button>
          
          {filterType === 'select' && filterOptions && (
            <select
              value={filters[field] || ''}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              className={`w-full text-xs border rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-kaleido-purple/30 transition-all ${
                hasFilter ? 'border-kaleido-purple bg-kaleido-purple/5 ring-1 ring-kaleido-purple/20' : 'border-kaleido-gray-200'
              }`}
            >
              <option value="">All</option>
              {filterOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          )}
          
          {filterType === 'text' && (
            <input
              type="text"
              value={filters[field] || ''}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              placeholder="Filter..."
              className={`w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-kaleido-purple/30 transition-all ${
                hasFilter ? 'border-kaleido-purple bg-kaleido-purple/5 ring-1 ring-kaleido-purple/20' : 'border-kaleido-gray-200'
              }`}
            />
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      {/* Full Screen Modal */}
      <div className="bg-white w-full h-full sm:w-[98vw] sm:h-[96vh] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        
        {/* Header Bar */}
        <div className="bg-gradient-to-r from-kaleido-purple/10 via-white to-kaleido-blue/10 border-b border-kaleido-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Title Section */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-kaleido-purple to-kaleido-blue flex items-center justify-center shadow-lg flex-shrink-0">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold text-kaleido-gray-900">Document Access Control</h1>
                <p className="text-xs sm:text-sm text-kaleido-gray-500 truncate">
                  <span className="font-semibold text-kaleido-gray-700">{user.display_name}</span>
                  <span className="hidden xs:inline mx-2 text-kaleido-gray-300">•</span>
                  <span className="hidden xs:inline text-kaleido-gray-400">{user.email}</span>
                </p>
              </div>
              {/* Mobile close button - positioned at end on mobile */}
              <button
                onClick={onClose}
                className="sm:hidden p-2 hover:bg-kaleido-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Close (Esc)"
              >
                <X className="h-5 w-5 text-kaleido-gray-400" />
              </button>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto pb-1 sm:pb-0 -mb-1 sm:mb-0">
              {/* Keyboard Shortcut Hint */}
              <div className="hidden xl:flex items-center gap-2 text-xs text-kaleido-gray-400 bg-kaleido-gray-50 px-3 py-1.5 rounded-lg border border-kaleido-gray-100">
                <Keyboard className="h-3 w-3" />
                <span>Shift+Click for range • Esc to close</span>
              </div>

              {/* Quick Stats - Compact on mobile */}
              <div className="flex items-center gap-2 sm:gap-4 bg-white rounded-xl px-2 sm:px-5 py-1.5 sm:py-2.5 border border-kaleido-gray-200 shadow-sm flex-shrink-0">
                <div className="text-center min-w-[40px] sm:min-w-[60px]">
                  <div className="text-base sm:text-2xl font-bold text-kaleido-purple">{selectedCount}</div>
                  <div className="text-[8px] sm:text-[10px] uppercase tracking-wider text-kaleido-gray-500 font-medium">Selected</div>
                </div>
                <div className="h-6 sm:h-10 w-px bg-kaleido-gray-200" />
                <div className="text-center min-w-[40px] sm:min-w-[60px]">
                  <div className="text-base sm:text-2xl font-bold text-kaleido-gray-700">{totalDocuments}</div>
                  <div className="text-[8px] sm:text-[10px] uppercase tracking-wider text-kaleido-gray-500 font-medium">Total</div>
                </div>
                {selectedCount > 0 && (
                  <>
                    <div className="h-6 sm:h-10 w-px bg-kaleido-gray-200" />
                    <div className="text-center min-w-[40px] sm:min-w-[60px]">
                      <div className="text-base sm:text-2xl font-bold text-green-600">{Math.round((selectedCount / totalDocuments) * 100)}%</div>
                      <div className="text-[8px] sm:text-[10px] uppercase tracking-wider text-kaleido-gray-500 font-medium">Coverage</div>
                    </div>
                  </>
                )}
              </div>

              {/* Desktop close button */}
              <button
                onClick={onClose}
                className="hidden sm:block p-2 hover:bg-kaleido-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Close (Esc)"
              >
                <X className="h-6 w-6 text-kaleido-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="bg-kaleido-gray-50 border-b border-kaleido-gray-200 px-3 sm:px-6 py-2 sm:py-3 flex-shrink-0">
          {/* Mobile: Search + Controls toggle */}
          <div className="flex items-center gap-2 lg:hidden mb-2">
            {/* Global Search - Mobile */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-kaleido-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search documents..."
                  className="w-full pl-10 pr-4 py-2 border border-kaleido-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kaleido-purple/20 focus:border-kaleido-purple bg-white"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-kaleido-gray-100 rounded"
                  >
                    <X className="h-3 w-3 text-kaleido-gray-400" />
                  </button>
                )}
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowMobileControls(!showMobileControls)} 
              className={`h-9 px-3 flex-shrink-0 ${showMobileControls ? 'bg-kaleido-purple/10 text-kaleido-purple border-kaleido-purple' : ''}`}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowSelectionSummary(!showSelectionSummary)} 
              className={`h-9 px-3 flex-shrink-0 ${showSelectionSummary ? 'bg-kaleido-purple/10 text-kaleido-purple border-kaleido-purple' : ''}`}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          {/* Mobile: Expanded Controls */}
          {showMobileControls && (
            <div className="lg:hidden space-y-3 pb-2 mb-2 border-b border-kaleido-gray-200">
              {/* Organization */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-kaleido-gray-500">Organization</label>
                <input
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  className="w-full px-3 py-2 border border-kaleido-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kaleido-purple/20 focus:border-kaleido-purple bg-white"
                  placeholder="e.g., AACE International"
                />
              </div>
              {/* Prompt Config */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-kaleido-gray-500">Prompt Configuration</label>
                <select
                  value={promptConfig}
                  onChange={(e) => setPromptConfig(e.target.value)}
                  className="w-full px-3 py-2 border border-kaleido-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kaleido-purple/20 focus:border-kaleido-purple bg-white"
                >
                  <option value="default">Default (BSI)</option>
                  <option value="aace">AACE International</option>
                  <option value="di">Decision Intelligence</option>
                </select>
              </div>
              {/* Self-Select Prompt Toggle */}
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs font-medium text-kaleido-gray-500">Allow user to select prompt set</label>
                <button
                  onClick={() => setCanSelectPrompt(!canSelectPrompt)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    canSelectPrompt ? 'bg-kaleido-purple' : 'bg-kaleido-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      canSelectPrompt ? 'translate-x-4.5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              {/* Can Create Prompts Toggle */}
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs font-medium text-kaleido-gray-500">Allow user to create prompt sets</label>
                <button
                  onClick={() => setCanCreatePrompts(!canCreatePrompts)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    canCreatePrompts ? 'bg-kaleido-purple' : 'bg-kaleido-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      canCreatePrompts ? 'translate-x-4.5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              {/* Show PDF Toggle */}
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs font-medium text-kaleido-gray-500">Allow PDF document viewing</label>
                <button
                  onClick={() => setShowPdf(!showPdf)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showPdf ? 'bg-kaleido-purple' : 'bg-kaleido-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      showPdf ? 'translate-x-4.5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              {/* Bulk Actions */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAllGlobal} className="text-xs h-9 flex-1">
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={handleClearAllGlobal} className="text-xs h-9 flex-1">
                  Clear All
                </Button>
              </div>
            </div>
          )}

          {/* Desktop Controls */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Organization */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-kaleido-gray-500 whitespace-nowrap">Organization:</label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="w-48 px-3 py-1.5 border border-kaleido-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kaleido-purple/20 focus:border-kaleido-purple bg-white"
                placeholder="e.g., AACE International"
              />
            </div>

            {/* Prompt Config */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-kaleido-gray-500 whitespace-nowrap">Prompt:</label>
              <select
                value={promptConfig}
                onChange={(e) => setPromptConfig(e.target.value)}
                className="w-48 px-3 py-1.5 border border-kaleido-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kaleido-purple/20 focus:border-kaleido-purple bg-white"
              >
                <option value="default">Default (BSI)</option>
                <option value="aace">AACE International</option>
                <option value="di">Decision Intelligence</option>
              </select>
            </div>

            {/* Self-Select Prompt Toggle */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-kaleido-gray-500 whitespace-nowrap">Self-select:</label>
              <button
                onClick={() => setCanSelectPrompt(!canSelectPrompt)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  canSelectPrompt ? 'bg-kaleido-purple' : 'bg-kaleido-gray-300'
                }`}
                title="Allow this user to select their own prompt set in the chat interface"
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    canSelectPrompt ? 'translate-x-4.5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Can Create Prompts Toggle */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-kaleido-gray-500 whitespace-nowrap">Create Prompts:</label>
              <button
                onClick={() => setCanCreatePrompts(!canCreatePrompts)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  canCreatePrompts ? 'bg-kaleido-purple' : 'bg-kaleido-gray-300'
                }`}
                title="Allow this user to create and manage their own prompt sets"
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    canCreatePrompts ? 'translate-x-4.5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Show PDF Toggle */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-kaleido-gray-500 whitespace-nowrap">Show PDF:</label>
              <button
                onClick={() => setShowPdf(!showPdf)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  showPdf ? 'bg-kaleido-purple' : 'bg-kaleido-gray-300'
                }`}
                title="Allow this user to view PDF versions of source documents"
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    showPdf ? 'translate-x-4.5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            <div className="h-6 w-px bg-kaleido-gray-300" />

            {/* Global Search */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-kaleido-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search all fields..."
                  className="w-full pl-10 pr-4 py-1.5 border border-kaleido-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kaleido-purple/20 focus:border-kaleido-purple bg-white"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-kaleido-gray-100 rounded"
                  >
                    <X className="h-3 w-3 text-kaleido-gray-400" />
                  </button>
                )}
              </div>
            </div>

            <div className="h-6 w-px bg-kaleido-gray-300" />

            {/* Bulk Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSelectAllGlobal} className="text-xs h-8">
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={handleClearAllGlobal} className="text-xs h-8">
                Clear All
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowSelectionSummary(!showSelectionSummary)} 
                className={`text-xs h-8 ${showSelectionSummary ? 'bg-kaleido-purple/10 text-kaleido-purple' : ''}`}
              >
                <Filter className="h-3 w-3 mr-1" />
                By Publisher
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="bg-kaleido-purple/5 border-b border-kaleido-purple/20 px-3 sm:px-6 py-2 sm:py-2.5 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <span className="text-xs font-medium text-kaleido-gray-500 hidden sm:inline">Active filters:</span>
              <div className="flex items-center gap-2 flex-wrap flex-1">
                {searchQuery && (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-kaleido-purple/30 text-kaleido-purple text-xs px-2 sm:px-2.5 py-1 rounded-full">
                    <span className="truncate max-w-[100px] sm:max-w-none">"{searchQuery}"</span>
                    <button onClick={() => setSearchQuery('')} className="hover:bg-kaleido-purple/10 rounded-full p-0.5 flex-shrink-0">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {activeFilters.map(([key, value]) => (
                  <span key={key} className="inline-flex items-center gap-1.5 bg-white border border-kaleido-purple/30 text-kaleido-purple text-xs px-2 sm:px-2.5 py-1 rounded-full">
                    <span className="truncate max-w-[80px] sm:max-w-none">{key}: {value}</span>
                    <button onClick={() => removeFilter(key)} className="hover:bg-kaleido-purple/10 rounded-full p-0.5 flex-shrink-0">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button 
                  onClick={clearAllFilters}
                  className="text-xs text-kaleido-gray-500 hover:text-red-600 flex items-center gap-1 ml-auto sm:ml-0"
                >
                  <RotateCcw className="h-3 w-3" />
                  <span className="hidden sm:inline">Clear all</span>
                </button>
              </div>
              <div className="hidden sm:block h-4 w-px bg-kaleido-gray-300" />
              <div className="flex items-center justify-between sm:justify-start gap-2 text-xs text-kaleido-gray-600">
                <span>
                  <strong className="text-kaleido-purple">{filteredCount}</strong> of {totalDocuments}
                  {selectedInFiltered > 0 && (
                    <span className="ml-1">• <strong className="text-green-600">{selectedInFiltered}</strong> sel.</span>
                  )}
                </span>
                {filteredCount > 0 && !allFilteredSelected && (
                  <button 
                    onClick={handleSelectAll}
                    className="text-xs text-kaleido-purple hover:underline"
                  >
                    Select filtered
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden flex relative">
          {/* Selection Summary Sidebar - Overlay on mobile, side panel on desktop */}
          {showSelectionSummary && (
            <>
              {/* Mobile backdrop */}
              <div 
                className="lg:hidden fixed inset-0 bg-black/40 z-20"
                onClick={() => setShowSelectionSummary(false)}
              />
              <div className="absolute lg:relative z-30 lg:z-auto top-0 left-0 h-full w-72 sm:w-80 lg:w-72 border-r border-kaleido-gray-200 bg-white flex-shrink-0 flex flex-col shadow-xl lg:shadow-none">
                <div className="px-4 py-3 border-b border-kaleido-gray-100 bg-kaleido-gray-50 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-kaleido-gray-900">Selection by Publisher</h3>
                    <p className="text-xs text-kaleido-gray-500 mt-0.5">Tap to select/deselect all</p>
                  </div>
                  <button 
                    onClick={() => setShowSelectionSummary(false)}
                    className="lg:hidden p-1.5 hover:bg-kaleido-gray-100 rounded-lg"
                  >
                    <X className="h-4 w-4 text-kaleido-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {selectionSummary.map(({ publisher, total, selected }) => {
                    const isFullySelected = selected === total;
                    const isPartiallySelected = selected > 0 && selected < total;
                    return (
                      <button
                        key={publisher}
                        onClick={() => handleSelectByPublisher(publisher)}
                        className={`w-full px-4 py-3 lg:py-2.5 flex items-center gap-3 text-left hover:bg-kaleido-gray-50 active:bg-kaleido-gray-100 transition-colors border-b border-kaleido-gray-50 ${
                          isFullySelected ? 'bg-kaleido-purple/5' : ''
                        }`}
                      >
                        <div className={`w-5 h-5 lg:w-4 lg:h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isFullySelected 
                            ? 'bg-kaleido-purple border-kaleido-purple' 
                            : isPartiallySelected
                            ? 'bg-kaleido-purple/30 border-kaleido-purple'
                            : 'border-kaleido-gray-300'
                        }`}>
                          {(isFullySelected || isPartiallySelected) && (
                            <Check className={`h-3 w-3 ${isFullySelected ? 'text-white' : 'text-kaleido-purple'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-kaleido-gray-800 truncate">{publisher}</div>
                          <div className="text-xs text-kaleido-gray-500">
                            {selected} / {total} selected
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-kaleido-gray-300 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Data Table / Card View */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {isLoadingDocuments ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 text-kaleido-purple animate-spin mb-4" />
                <p className="text-kaleido-gray-500 font-medium">Loading document library...</p>
              </div>
            ) : filteredAndSortedDocuments.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center px-4">
                <FileText className="h-12 sm:h-16 w-12 sm:w-16 text-kaleido-gray-200 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-kaleido-gray-900 mb-2 text-center">No Documents Found</h3>
                <p className="text-sm text-kaleido-gray-500 mb-4 text-center">
                  No documents match your current search and filter criteria.
                </p>
                <Button variant="outline" onClick={clearAllFilters}>
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="md:hidden flex-1 overflow-y-auto p-3 space-y-2">
                  {/* Select All Header */}
                  <div className="flex items-center justify-between bg-kaleido-gray-50 rounded-lg px-3 py-2 mb-2 sticky top-0 z-10">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleSelectNone()}
                        className="w-5 h-5 rounded border-kaleido-gray-300 text-kaleido-purple focus:ring-kaleido-purple"
                      />
                      <span className="text-sm font-medium text-kaleido-gray-600">Select all visible</span>
                    </label>
                    <span className="text-xs text-kaleido-gray-500">{filteredCount} docs</span>
                  </div>
                  
                  {filteredAndSortedDocuments.map((doc, index) => {
                    const isSelected = selectedDocuments.includes(doc.canonical_slug);
                    return (
                      <div
                        key={doc.canonical_slug}
                        onClick={(e) => handleToggleDocument(doc.canonical_slug, index, e)}
                        className={`relative rounded-xl border-2 p-3 cursor-pointer transition-all active:scale-[0.98] ${
                          isSelected 
                            ? 'border-kaleido-purple bg-kaleido-purple/5 shadow-sm' 
                            : 'border-kaleido-gray-100 bg-white hover:border-kaleido-gray-200'
                        }`}
                      >
                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute top-0 left-0 w-1 h-full bg-kaleido-purple rounded-l-xl" />
                        )}
                        
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                            isSelected 
                              ? 'bg-kaleido-purple border-kaleido-purple' 
                              : 'border-kaleido-gray-300'
                          }`}>
                            {isSelected && <Check className="h-4 w-4 text-white" />}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {/* Publisher badge */}
                            {doc.publishing_body && (
                              <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-kaleido-purple bg-kaleido-purple/10 px-2 py-0.5 rounded-full mb-1.5">
                                {doc.publishing_body}
                              </span>
                            )}
                            
                            {/* Title */}
                            <h4 className={`text-sm font-semibold leading-snug mb-1 ${isSelected ? 'text-kaleido-purple' : 'text-kaleido-gray-900'}`}>
                              {doc.document_title || doc.source_filename}
                            </h4>
                            
                            {/* Reference */}
                            {doc.document_reference && (
                              <span className="inline-block text-xs font-mono text-kaleido-gray-500 bg-kaleido-gray-100 px-1.5 py-0.5 rounded mb-1.5">
                                {doc.document_reference}
                              </span>
                            )}
                            
                            {/* Meta row */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-kaleido-gray-500">
                              {doc.authors && (
                                <span className="truncate max-w-[150px]">{doc.authors}</span>
                              )}
                              {doc.publication_date && (
                                <span>{doc.publication_date.substring(0, 4)}</span>
                              )}
                              {doc.country_of_origin && (
                                <span>{doc.country_of_origin}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block flex-1 overflow-auto">
                  <table className="w-full border-collapse min-w-[1200px] lg:min-w-[1400px]">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="w-14 px-3 py-3 text-center bg-kaleido-gray-50 sticky left-0 z-20 border-r border-kaleido-gray-200">
                          <div className="space-y-2">
                            <span className="text-xs font-semibold text-kaleido-gray-500 uppercase tracking-wider">Sel</span>
                            <div className="flex justify-center">
                              <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                onChange={(e) => e.target.checked ? handleSelectAll() : handleSelectNone()}
                                className="w-4 h-4 rounded border-kaleido-gray-300 text-kaleido-purple focus:ring-kaleido-purple cursor-pointer"
                              />
                            </div>
                          </div>
                        </th>
                        <ColumnHeader field="publisher" label="Publisher" width="w-48" filterType="select" filterOptions={uniqueValues.publishers} />
                        <ColumnHeader field="title" label="Document Title" width="min-w-[300px]" filterType="text" />
                        <ColumnHeader field="reference" label="Reference" width="w-32" filterType="text" />
                        <ColumnHeader field="authors" label="Author(s)" width="w-44" filterType="select" filterOptions={uniqueValues.authors} />
                        <ColumnHeader field="date" label="Published" width="w-24" filterType="select" filterOptions={uniqueValues.years} />
                        <ColumnHeader field="country" label="Country" width="w-32" filterType="select" filterOptions={uniqueValues.countries} />
                        <ColumnHeader field="tag" label="Tags" width="w-40" filterType="select" filterOptions={uniqueValues.tags} />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedDocuments.map((doc, index) => {
                        const isSelected = selectedDocuments.includes(doc.canonical_slug);
                        return (
                          <tr 
                            key={doc.canonical_slug}
                            onClick={(e) => handleToggleDocument(doc.canonical_slug, index, e)}
                            className={`cursor-pointer transition-all duration-100 group ${
                              isSelected 
                                ? 'bg-kaleido-purple/[0.07]' 
                                : index % 2 === 0 ? 'bg-white' : 'bg-kaleido-gray-50/50'
                            } hover:bg-kaleido-purple/10`}
                          >
                            <td className={`px-3 py-2.5 text-center sticky left-0 z-10 border-r border-kaleido-gray-100 ${
                              isSelected 
                                ? 'bg-kaleido-purple/[0.07]' 
                                : index % 2 === 0 ? 'bg-white' : 'bg-kaleido-gray-50/50'
                            } group-hover:bg-kaleido-purple/10`}>
                              <div className="relative">
                                {isSelected && (
                                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-6 bg-kaleido-purple rounded-r" />
                                )}
                                <div className={`w-5 h-5 mx-auto rounded border-2 flex items-center justify-center transition-all ${
                                  isSelected 
                                    ? 'bg-kaleido-purple border-kaleido-purple scale-110' 
                                    : 'border-kaleido-gray-300 group-hover:border-kaleido-purple/50'
                                }`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-sm font-medium text-kaleido-gray-700 line-clamp-1" title={doc.publishing_body}>
                                {doc.publishing_body || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="text-sm font-medium text-kaleido-gray-900 line-clamp-1 group-hover:text-kaleido-purple transition-colors" title={doc.document_title}>
                                {doc.document_title || doc.source_filename}
                              </div>
                              {doc.description && (
                                <div className="text-xs text-kaleido-gray-400 line-clamp-1 mt-0.5" title={doc.description}>
                                  {doc.description}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-xs font-mono text-kaleido-gray-600 bg-kaleido-gray-100 px-1.5 py-0.5 rounded" title={doc.document_reference}>
                                {doc.document_reference || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-sm text-kaleido-gray-600 line-clamp-1" title={doc.authors}>
                                {doc.authors || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-sm text-kaleido-gray-600">
                                {doc.publication_date || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-sm text-kaleido-gray-600 line-clamp-1" title={doc.country_of_origin}>
                                {doc.country_of_origin || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex flex-wrap gap-1">
                                {(doc.tags || []).length > 0 ? doc.tags.map(tag => (
                                  <span key={tag} className="text-xs bg-kaleido-purple/10 text-kaleido-purple px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                    {tag}
                                  </span>
                                )) : <span className="text-sm text-kaleido-gray-400">-</span>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-kaleido-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Status Message */}
            <div className="flex-1">
              {selectedCount === 0 ? (
                <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-amber-200">
                  <AlertTriangle className="h-4 sm:h-5 w-4 sm:w-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium">No documents selected — public access only</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-green-200">
                  <CheckCircle2 className="h-4 sm:h-5 w-4 sm:w-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium">
                    {selectedCount} document{selectedCount !== 1 ? 's' : ''} selected
                    <span className="text-green-600/70 ml-1">
                      ({Math.round((selectedCount / totalDocuments) * 100)}% of library)
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 sm:flex-none px-4 sm:px-6 h-10 sm:h-10"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1 sm:flex-none px-4 sm:px-8 h-10 sm:h-10 bg-gradient-to-r from-kaleido-purple to-kaleido-blue hover:opacity-90 shadow-lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="hidden sm:inline">Saving...</span>
                    <span className="sm:hidden">Save</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentAccessModal;
